/**
 * Redux actions and asynchronous thunks for managing transcription-related operations
 * in the QC Detail Page of the application.
 */

import { createAction, createAsyncThunk } from "@reduxjs/toolkit";
import { del, get, patch, post, put, putPresigned } from "../../../services/API/ApiClient";
import { RootState } from "../../store";
import { TranscriptionBlockType } from "../../../utils/type";
import { highlightedTextType } from "./QCDetailPageSlice";
import { openErrorNotification, openNotification } from "../../../components/Template/NotificationComponent";

// Fetch transcription pack details, media tokens, and DRM tokens
export const fetchDetailPack = createAsyncThunk<any, any, { state: RootState }>("transcribe/fetchCompletedAudios", async (id, { rejectWithValue, dispatch }) => {
  try {
    const response = await get(`/packs/${id}`, "Transcribe");
    const responseToken = await get(`/token/access/${response.data.transcriptions[0].fileName}?TenantID=${response.data.idTenant}`, "Transcribe");
    const responseBody = JSON.parse(responseToken.data.body);
    const responseDrmToken = await get(`/token`, "Transcribe");
    const responseDrmBody = JSON.parse(responseDrmToken.data.body);

    const preProcessBlock = response.data.transcriptions[0].transcriptionBlocks.map((block: TranscriptionBlockType) => ({
      ...block,
      isSelected: false,
      showed: false,
    }));

    const newPreData = {
      ...response.data,
      transcriptions: [
        {
          ...response.data.transcriptions[0],
          transcriptionBlocks: preProcessBlock,
        },
      ],
    };

    return { data: newPreData, mediaToken: responseBody, drmToken: responseDrmBody };
  } catch (e) {
    return rejectWithValue(e);
  }
});

// Fetch only transcription pack details
export const fetchPackDetailOnly = createAsyncThunk<any, any, { state: RootState }>("transcribe/fetchPackDetailOnly", async (id, { rejectWithValue }) => {
  try {
    const response = await get(`/packs/${id}`, "Transcribe");
    return response.data;
  } catch (e) {
    return rejectWithValue(e);
  }
});

export const completeReview = createAsyncThunk<any, any, { state: RootState }>("transcribe/completeReview", async (args, { rejectWithValue }) => {
  try {
    const response = await patch(`/packs/${args.PackId}/review-complete`, "Transcribe", args);
    openNotification("Complete Review", "Pack review completed successfully", "top");
    return response.data;
  } catch (e) {
    openErrorNotification("Complete Review", "Pack review failed", "top");
    return rejectWithValue(e);
  }
});
export const reopenForPreview = createAsyncThunk<any, any, { state: RootState }>("transcribe/reopenForPreview", async (id, { getState, dispatch, rejectWithValue }) => {
  try {
    const response = await patch(`/packs/${id}/re-open`, "Transcribe", {});
    openNotification("Reopen Preview", "Reopened pack for preview successfully", "top");
    return response;
  } catch (e) {
    openErrorNotification("Reopen Preview", "Failed to reopen pack for preview", "top");
    return rejectWithValue(e);
  }
});

export const updateText = createAction("transcribe/updateText", (updatedText: TranscriptionBlockType[]) => {
  return {
    payload: {
      updatedText,
    },
  };
});

// Set replace text for find and replace functionality
export const setReplaceText = createAction("transcribe/setReplaceText", (replaceText: string) => ({
  payload: { replaceText },
}));

// Set search query for find functionality
export const setSearchQuery = createAction("transcribe/setSearchQuery", (searchQuery: string) => ({
  payload: { searchQuery },
}));

// Set all matches for search results
export const setAllMatches = createAction("transcribe/setAllMatches", (allMatches: any) => ({
  payload: { allMatches },
}));

// Set the current match index for search results
export const setCurrentMatchIndex = createAction("transcribe/setCurrentMatchIndex", (currentMatchIndex: number) => ({
  payload: { currentMatchIndex },
}));

// Fetch media token for transcription playback
export const fetchMediaToken = createAsyncThunk<any, any, { state: RootState }>("transcribe/fetchMediaToken", async (id, { rejectWithValue }) => {
  try {
    const response = await get(`/packs/${id}/media-token`, "Transcribe");
    return response.data;
  } catch (e) {
    return rejectWithValue(e);
  }
});

// Set the current playback time
export const setCurrentTimeValue = createAction("transcribe/setCurrentTimeValue", (time: number) => ({
  payload: { time },
}));

// Set playback state (playing or paused)
export const setPlaying = createAction("transcribe/setPlaying", (playing: boolean) => ({
  payload: { playing },
}));

// Update speaker information
export const updateSpeaker = createAsyncThunk<any, any, { state: RootState }>("transcribe/updateSpeaker", async (args, { rejectWithValue, dispatch }) => {
  try {
    dispatch(clearMergeBlock());
    const response = await patch(`/transcriptions/${args.id}`, "Transcribe", args.data);
    openNotification("Update Speaker", "Updated speaker information successfully", "top");
    return {
      speakers: response.data.speakers,
      transcriptionBlocks: response.data.transcriptionBlocks,
    };
  } catch (e) {
    openErrorNotification("Update Speaker", "Failed to update speaker information", "top");
    return rejectWithValue(e);
  }
});

export const updateTime = createAsyncThunk<any, any, { state: RootState }>("transcribe/updateTime", async (args, { rejectWithValue }) => {
  try {
    const response = await patch(`/transcriptions/${args.id}/block`, "Transcribe", args.data);
    openNotification("Update Time", "Updated time information successfully", "top");
    return {
      speakers: response.data.speakers,
      transcriptionBlocks: response.data.transcriptionBlocks,
    };
  } catch (e) {
    openErrorNotification("Update Time", "Failed to update time information", "top");
    return rejectWithValue(e);
  }
});

//Get Template Options
export const getTemplateOptions = createAsyncThunk<any, { id: any; type: any }, { state: RootState }>("transcribe/getTemplateOptions", async ({ id, type }, { getState, dispatch, rejectWithValue }) => {
  try {
    const response = await get(`/data/export-templates?idTenant=${id}&transcriptionType=${type}`, "Transcribe");
    const templateOptions = [
      ...response.data.map((template: any) => {
        return { label: template.name, value: template.id };
      }),
    ];
    return templateOptions;
  } catch (e) {
    return rejectWithValue(e);
  }
});

// Export
export const exportTemplate = createAsyncThunk<any, { id: any; type: any }, { state: RootState }>("transcribe/exportTemplate", async ({ id, type }, { getState, dispatch, rejectWithValue }) => {
  try {
    const response = await put(`/exports/transcriptions/${id}?exportTemplateId=${type}`, "Transcribe", {});

    return response;
  } catch (e) {
    return rejectWithValue(e);
  }
});

// Handle merging of transcription blocks
export const handleMergeBlock = createAction("transcribe/handleMergeBlock", (blockIndex: number) => ({
  payload: { blockIndex },
}));

// Handle selection for merging transcription blocks
export const handleMergeBlockSelect = createAction("transcribe/handMergeBlockSelect", (blockIndex: number) => ({
  payload: { blockIndex },
}));

// Merge transcription blocks
export const mergeBlock = createAsyncThunk<any, any, { state: RootState }>("transcribe/mergeBlock", async (args, { rejectWithValue }) => {
  try {
    const response = await patch(`/transcriptions/${args.TranscriptionId}/merge-blocks`, "Transcribe", args);
    openNotification("Merge Blocks", "Merged transcription blocks successfully", "top");
    return {
      speakers: response.data.speakers,
      transcriptionBlocks: response.data.transcriptionBlocks,
    };
  } catch (e) {
    openErrorNotification("Merge Blocks", "Failed to merge transcription blocks", "top");
    return rejectWithValue(e);
  }
});
// Update a transcription block
export const updateBlock = createAsyncThunk<any, any, { state: RootState }>("transcribe/updateBlock", async (args, { rejectWithValue }) => {
  try {
    const response = await patch(`/transcriptions/${args.id}/block`, "Transcribe", args.data);
    openNotification("Update Block", "Updated transcription block successfully", "top");
    return {
      speakers: response.data.speakers,
      transcriptionBlocks: response.data.transcriptionBlocks,
      blockIndex: args.blockIndex,
    };
  } catch (e) {
    openErrorNotification("Update Block", "Failed to update transcription block", "top");
    return rejectWithValue(e);
  }
});
// Delete a transcription block
export const deleteBlock = createAsyncThunk<any, any, { state: RootState }>("transcribe/deleteBlock", async (args, { rejectWithValue }) => {
  try {
    const response = await del(`/transcriptions/${args.idTranscription}/block/${args.idBlock}`, "Transcribe");
    openNotification("Delete Block", "Deleted transcription block successfully", "top");
    return {
      speakers: response.data.speakers,
      transcriptionBlocks: response.data.transcriptionBlocks,
    };
  } catch (e) {
    openErrorNotification("Delete Block", "Failed to delete transcription block", "top");
    return rejectWithValue(e);
  }
});

// Create a new transcription segment
export const createNewSegment = createAsyncThunk<any, any, { state: RootState }>("transcribe/createNewSegment", async (args, { rejectWithValue }) => {
  try {
    const response = await post(`/transcriptions/${args.id}/block`, "Transcribe", args.data);
    openNotification("Create Segment", "Created new transcription segment successfully", "top");
    return {
      speakers: response.data.speakers,
      transcriptionBlocks: response.data.transcriptionBlocks,
    };
  } catch (e) {
    openErrorNotification("Create Segment", "Failed to create new transcription segment", "top");
    return rejectWithValue(e);
  }
});

// Split a transcription segment
export const splitSegment = createAsyncThunk<any, any, { state: RootState }>("transcribe/splitSegment", async (args, { rejectWithValue }) => {
  try {
    const response = await post(`/transcriptions/${args.id}/block`, "Transcribe", args.data);
    openNotification("Split Segment", "Split transcription segment successfully", "top");
    return {
      speakers: response.data.speakers,
      transcriptionBlocks: response.data.transcriptionBlocks,
    };
  } catch (e) {
    openErrorNotification("Split Segment", "Failed to split transcription segment", "top");
    return rejectWithValue(e);
  }
});

//Action for replace all

export const replaceAll = createAsyncThunk<any, any, { state: RootState }>("transcribe/replaceAll", async (args, { rejectWithValue }) => {
  try {
    const response = await patch(`/transcriptions/${args.id}/replace-all`, "Transcribe", args.data);
    openNotification("Replace All", "Replaced all occurrences successfully", "top");
    return {
      speakers: response.data.speakers,
      transcriptionBlocks: response.data.transcriptionBlocks,
    };
  } catch (e) {
    openErrorNotification("Replace All", "Failed to replace all occurrences", "top");
    return rejectWithValue(e);
  }
});

export const addFindHistory = createAction("transcribe/addFindHistory", (history: string) => ({
  payload: { history },
}));

export const addReplaceHistory = createAction("transcribe/addReplaceHistory", (history: string) => ({
  payload: { history },
}));

// Set highlighted text for find and replace functionality
export const setHighlightText = createAction("transcribe/setHighlightText", (highlightText: highlightedTextType) => ({
  payload: { highlightText },
}));

// Clear merge block selection
export const clearMergeBlock = createAction("transcribe/clearMergeBlock");

export const emptyMergeBlock = createAction("transcribe/emptyMergeBlock");
export const resetHighlightText = createAction("transcribe/resetHighlightText");
// Toggle closed captions (CC)
export const switchCC = createAction("transcribe/switchCC");

// Toggle word-level highlighting
export const switchW = createAction("transcribe/switchW");

// Reset the QC Detail Page state
export const resetQCDetailPage = createAction("transcribe/resetQCDetailPage");

// Open the find and replace modal
export const openFindReplace = createAction("transcribe/openFindReplace");

// Close the find and replace modal
export const closeFindReplace = createAction("transcribe/closeFindReplace");
// Open the delete block modal
export const openQcDetailDeleteModal = createAction("transcribe/openQcDetailDeleteModal");

// Close the delete block modal
export const closeQcDetailDeleteModal = createAction("transcribe/closeQcDetailDeleteModal");

// Open the edit speaker modal
export const openEditSpeakerModal = createAction("transcribe/openEditSpeakerModal");

// Close the edit speaker modal
export const closeEditSpeakerModal = createAction("transcribe/closeEditSpeakerModal");

// Set the end time for playback
export const setEndTime = createAction("transcribe/setEndTime", (endTime: number) => ({
  payload: { endTime },
}));
export const setCurrentFocusBlockIndex = createAction("transcribe/setCurrentFocusBlockIndex", (currentFocusBlockIndex: number) => ({
  payload: { currentFocusBlockIndex },
}));
//Open the delete Audio block modal
export const openQcDetailAudioBlockDeleteModal = createAction("transcribe/openQcDetailAudioBlockDeleteModal");

//Close the delete Audio block modal
export const closeQcDetailAudioBlockDeleteModal = createAction("transcribe/closeQcDetailAudioBlockDeleteModal");

export const openCompletedReviewModal = createAction("transcribe/openCompletedReviewModal");
export const closeCompletedReviewModal = createAction("transcribe/closeCompletedReviewModal");

//Select transcription Block
export const setSelectedTranscriptionBlock = createAction<number>("transcribe/setSelectedTranscriptionBlock");
export const openExportModal = createAction("transcribe/openExportModal");
export const closeExportModal = createAction("transcribe/closeExportModal");
